import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

export default class extends Controller {
  static targets = ["chart", "pendingPlatform"]

  connect() {
    this.charts = new Map()
    this.drawCharts()
    this.element.querySelectorAll(".rp-compare-form").forEach(form => this.limitComparisonMonths(form))
    this.themeObserver = new MutationObserver(() => this.refreshTheme())
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-admin-theme", "data-theme", "class"] })
    this.themeObserver.observe(document.body, { attributes: true, attributeFilter: ["data-admin-theme", "data-theme", "class"] })
    const themedAncestor = this.element.closest("[data-theme]")
    if (themedAncestor && themedAncestor !== document.documentElement && themedAncestor !== document.body) {
      this.themeObserver.observe(themedAncestor, { attributes: true, attributeFilter: ["data-theme", "class"] })
    }
    this.beforeCache = () => this.destroyCharts()
    document.addEventListener("turbo:before-cache", this.beforeCache)
  }

  disconnect() {
    clearTimeout(this.platformTimer)
    this.themeObserver?.disconnect()
    document.removeEventListener("turbo:before-cache", this.beforeCache)
    this.destroyCharts()
  }

  pendingPlatformTargetConnected(element) {
    this.platformPolls = (this.platformPolls || 0) + 1
    if (this.platformPolls > 36) {
      element.hidden = false
      element.className = "rp-age-note"
      element.textContent = "Stripe preparation is taking longer than expected. "
      const retry = document.createElement("button")
      retry.type = "button"
      retry.className = "rp-action"
      retry.textContent = "Check again"
      retry.addEventListener("click", () => {
        this.platformPolls = 0
        element.closest("turbo-frame")?.reload()
      })
      element.append(retry)
      return
    }
    clearTimeout(this.platformTimer)
    this.platformTimer = setTimeout(() => {
      const frame = element.closest("turbo-frame")
      if (!frame?.isConnected) return
      if (frame.src === element.dataset.url || frame.src === new URL(element.dataset.url, location.origin).href) frame.reload()
      else frame.src = element.dataset.url
    }, 8000)
  }

  pendingPlatformTargetDisconnected() { clearTimeout(this.platformTimer) }

  chartTargetConnected() {
    // Stripe replaces the report frame after the outer controller connects.
    if (this.charts) this.drawCharts()
  }

  chartTargetDisconnected(canvas) {
    this.charts?.get(canvas)?.destroy()
    this.charts?.delete(canvas)
  }

  submit(event) {
    const form = event.currentTarget.form
    if (form?.reportValidity()) form.requestSubmit()
  }

  compareMonth(event) {
    const form = event.currentTarget.form
    if (!form) return
    this.limitComparisonMonths(form)
    for (const key of ["a", "b"]) {
      const month = form.querySelector(`[data-compare-month="${key}"]`)
      const year = form.querySelector(`[data-compare-year="${key}"]`)
      if (month && year && form.elements[`month_${key}`]) form.elements[`month_${key}`].value = `${year.value}-${month.value}`
    }
    if (form.reportValidity()) form.requestSubmit()
  }

  limitComparisonMonths(form) {
    const asOf = form.elements.as_of?.value || ""
    const [lastYear, lastMonth] = asOf.split("-").map(Number)
    if (!lastYear || !lastMonth) return
    for (const key of ["a", "b"]) {
      const month = form.querySelector(`[data-compare-month="${key}"]`)
      const year = form.querySelector(`[data-compare-year="${key}"]`)
      if (!month || !year) continue
      Array.from(month.options).forEach(option => { option.disabled = Number(year.value) === lastYear && Number(option.value) > lastMonth })
      if (Number(year.value) === lastYear && Number(month.value) > lastMonth) month.value = String(lastMonth).padStart(2, "0")
    }
  }

  navigate(event) {
    const destination = new URL(event.currentTarget.value, window.location.origin)
    if (destination.origin === window.location.origin && destination.pathname === "/admin/reports") {
      window.Turbo ? window.Turbo.visit(destination.href) : window.location.assign(destination.href)
    }
  }

  colors() {
    const style = getComputedStyle(this.element)
    const color = (name, fallback) => style.getPropertyValue(name).trim() || fallback
    return {
      palette: [color("--admin-accent", "#0069a8"), "#78c4e8", "#38aaa2", "#d99c35", "#dd719b", "#8b9cab"],
      semantic: {
        accent: color("--rp-chart-accent", "#0069a8"), sky: color("--rp-chart-sky", "#78c4e8"),
        teal: color("--rp-chart-teal", "#38aaa2"), amber: color("--rp-chart-amber", "#d99c35"),
        pink: color("--rp-chart-pink", "#dd719b"), muted: color("--rp-chart-muted", "#8b9cab")
      },
      text: color("--admin-text-3", "#88a0af"),
      border: color("--admin-border", "#e3edf4"),
      foreground: color("--admin-text", "#0e2230"),
      surface: color("--admin-surface", "#ffffff")
    }
  }

  drawCharts() {
    this.chartTargets.forEach(canvas => {
      if (this.charts.has(canvas)) return
      let data
      try { data = JSON.parse(canvas.dataset.chart) } catch { return }
      if (!Array.isArray(data.series) || !Array.isArray(data.labels)) return
      const colors = this.colors()
      const type = data.type === "line" ? "line" : "bar"
      const stacked = Boolean(data.stacked)
      const amountRanges = data.labels.some(label => typeof label === "string" && /^\$\d+–\d/.test(label))
      const datasets = data.series.map((series, index) => {
        const color = colors.semantic[series.color_key] || colors.palette[index % colors.palette.length]
        const currency = series.currency || data.currency
        return {
          label: series.name,
          data: series.values.map(value => typeof value === "number" && Number.isFinite(value) ? value : null),
          borderColor: color,
          backgroundColor: type === "line" ? this.alphaColor(color, 0.09) : color,
          borderWidth: type === "line" ? 2.5 : 0,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0,
          fill: type === "line" && index === 0 ? "origin" : false,
          spanGaps: false,
          borderRadius: type === "bar" ? 5 : 0,
          barPercentage: 1,
          categoryPercentage: 0.74,
          borderDash: series.dashed ? [5, 4] : [],
          currency,
          format: series.format || data.format || "number",
          // CAD and USD may share an axis, but are never added into one bar.
          ...(stacked ? { stack: currency || "counts" } : {})
        }
      })
      const chart = new Chart(canvas, {
        type,
        data: { labels: data.labels, datasets },
        plugins: [this.barTotalsPlugin(data, colors)],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          interaction: { mode: "index", intersect: false },
          layout: { padding: { top: type === "bar" ? 21 : 5, right: 4 } },
          plugins: {
            legend: {
              display: false,
              labels: { color: colors.text, boxWidth: 13, boxHeight: 7, padding: 15, usePointStyle: false, font: { family: "Manrope", size: 12, weight: "600" } }
            },
            tooltip: {
              titleFont: { family: "Manrope", size: 14.4 },
              bodyFont: { family: "Manrope", size: 14.4 },
              footerFont: { family: "Manrope", size: 14.4 },
              backgroundColor: colors.surface,
              titleColor: colors.foreground,
              bodyColor: colors.foreground,
              borderColor: colors.border,
              borderWidth: 1,
              padding: 10,
              callbacks: { label: item => `${item.dataset.label}: ${this.formatValue(item.raw, item.dataset.format, item.dataset.currency)}` }
            }
          },
          scales: {
            x: {
              stacked,
              border: { display: false },
              grid: { display: false, drawTicks: false },
              ticks: { color: colors.text, maxRotation: 0, autoSkip: !amountRanges, maxTicksLimit: 13, padding: 8, font: { family: "Manrope", size: 13.2, weight: "700" }, callback: (_, index) => this.axisLabel(data.labels[index], data.compact_labels, amountRanges) }
            },
            y: {
              display: type === "line",
              stacked,
              border: { display: false, dash: [3, 4] },
              grid: { color: colors.border, drawTicks: false },
              ticks: { color: colors.text, maxTicksLimit: 3, padding: 8, font: { family: "Manrope", size: 13.2, weight: "700" }, callback: value => this.axisValue(value, data.format) },
              beginAtZero: true,
              grace: type === "bar" ? "10%" : "4%"
            }
          }
        }
      })
      this.charts.set(canvas, chart)
    })
  }

  axisLabel(label, compact, amountRanges = false) {
    if (!compact || typeof label !== "string") return label
    // Keep every gift-size bucket legible on phones instead of skipping alternate ranges.
    if (amountRanges) {
      if (/^\$\d+–\d/.test(label)) {
        const [lower, upper] = label.split("–")
        return [`${lower}–`, upper]
      }
      if (label.startsWith("Under $")) return ["Under", label.slice(6)]
    }
    // Month names carry the year in the heading and accessible table.
    if (/^[A-Za-z]{3} \d{4}$/.test(label)) return label.slice(0, 3)
    if (/^\d{2}:00$/.test(label)) {
      const hour = Number(label.slice(0, 2))
      return `${hour % 12 || 12}${hour < 12 ? "a" : "p"}`
    }
    return label
  }

  alphaColor(color, alpha) {
    const hex = color.match(/^#([\da-f]{6})$/i)
    if (hex) return `rgba(${[0, 2, 4].map(offset => parseInt(hex[1].slice(offset, offset + 2), 16)).join(",")},${alpha})`
    const rgb = color.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/)
    return rgb ? `rgba(${rgb[1]},${rgb[2]},${rgb[3]},${alpha})` : "rgba(0,105,168,0.09)"
  }

  barTotalsPlugin(data, colors) {
    return {
      id: "reportBarTotals",
      afterDatasetsDraw: chart => {
        if (chart.config.type !== "bar" || data.totals === false || data.labels.length > 14) return
        const groups = new Map()
        chart.data.datasets.forEach((dataset, index) => {
          const key = data.stacked ? (dataset.stack || "counts") : index
          if (!groups.has(key)) groups.set(key, [])
          groups.get(key).push(index)
        })
        // Avoid unreadable totals on crowded grouped series. Hover exposes exact values.
        if (groups.size > 2 || chart.width / data.labels.length / groups.size < 35) return
        const ctx = chart.ctx
        ctx.save()
        ctx.font = "800 12.6px Manrope"
        ctx.textAlign = "center"
        ctx.textBaseline = "bottom"
        ctx.fillStyle = colors.foreground
        for (const indices of groups.values()) {
          data.labels.forEach((_, position) => {
            const values = indices.map(index => chart.data.datasets[index].data[position]).filter(value => typeof value === "number")
            if (!values.length || values.some(value => value < 0)) return
            const total = values.reduce((sum, value) => sum + value, 0)
            const elements = indices.map(index => chart.getDatasetMeta(index).data[position]).filter(Boolean)
            if (!elements.length) return
            const x = elements[0].x
            const y = Math.min(...elements.map(element => element.y)) - 5
            ctx.fillText(this.axisValue(total, data.format), x, y)
          })
        }
        ctx.restore()
      }
    }
  }

  axisValue(value, format) {
    const amount = Number(value)
    if (!Number.isFinite(amount)) return value
    if (["money", "money2"].includes(format)) {
      const label = new Intl.NumberFormat("en-CA", { notation: "compact", maximumFractionDigits: 1 }).format(Math.abs(amount))
      return `${amount < 0 ? "-" : ""}$${label}`
    }
    const label = format === "decimal" ? new Intl.NumberFormat("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) : new Intl.NumberFormat("en-CA", { notation: "compact", maximumFractionDigits: 1 }).format(amount)
    return ["percent", "pct"].includes(format) ? `${label}%` : label
  }

  formatValue(value, format, currency) {
    if (value == null) return "Not available"
    if (["money", "money2"].includes(format)) {
      return `${currency ? `${currency} ` : ""}${value < 0 ? "-" : ""}$${new Intl.NumberFormat("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(value))}`
    }
    const label = new Intl.NumberFormat("en-CA", { maximumFractionDigits: format === "number" ? 0 : (format === "decimal" ? 2 : 1) }).format(value)
    return ["percent", "pct"].includes(format) ? `${label}%` : label
  }

  refreshTheme() {
    this.destroyCharts()
    this.drawCharts()
  }

  destroyCharts() {
    this.charts?.forEach(chart => chart.destroy())
    this.charts?.clear()
  }
}
